/**
 * SDMS Template Builder — in-browser sheet edits.
 *
 * The designer sheet is diffed against the snapshot taken right after load, and
 * ONLY what the designer changed is sent to the server, which patches the .xlsx
 * surgically (SdmsWorkbookPatcher / XlsxSheetEditsWriter). Styles travel as DELTAS —
 * just the properties that differ — so everything the browser loader represents
 * approximately (theme colours, the normalised black borders, italic it does not
 * map, …) stays exactly as the workbook has it unless the designer touches it.
 *
 * Pure functions over Univer IWorkbookData snapshots (loosely typed) — unit-tested.
 */

// ── wire types (mirror Application/SdmsTemplateBuilder/SdmsSheetEdits.cs) ────

export interface FontDelta { name?: string; size?: number; bold?: boolean; italic?: boolean; underline?: boolean; strike?: boolean; color?: string }
export interface BorderSide { style: string; color?: string }
export interface StyleDelta {
  font?: FontDelta;
  /** present = change the fill; color null = no fill */
  fill?: { color: string | null };
  border?: { top?: BorderSide; bottom?: BorderSide; left?: BorderSide; right?: BorderSide };
  alignment?: { horizontal?: string; vertical?: string; wrap?: boolean };
  numberFormat?: string;
}
export interface CellEdit {
  sheetIndex: number;
  ref: string;
  contentChanged: boolean;
  formula?: string | null;
  value?: string | null;
  valueType?: 'string' | 'number' | 'boolean';
  oldText?: string | null;
  style?: StyleDelta;
}
export interface MergeEdit { sheetIndex: number; range: string; remove: boolean }
export interface SizeEdit { sheetIndex: number; index: number; pixels: number }
export interface ConditionalFormatEdit {
  sheetIndex: number;
  action: 'add' | 'update' | 'remove';
  /** one or more space-separated A1 ranges; update: omitted = keep */
  range?: string;
  /** update / add: the new position (1 = evaluated first) */
  newPriority?: number;
  stopIfTrue?: boolean;
  /** audit wording */
  description?: string;
  oldDescription?: string;
  type?: 'cellIs' | 'containsText' | 'expression';
  operator?: string;
  formula1?: string;
  formula2?: string;
  text?: string;
  fontColor?: string;
  fillColor?: string;
  bold?: boolean;
  priority?: number;
}
/** Excel data-validation rule (mirrors SdmsDataValidationEdit). An update carries the whole rule. */
export interface DataValidationEdit {
  sheetIndex: number;
  action: 'add' | 'update' | 'remove';
  /** update / remove: the rule's ORIGINAL 0-based position in the worksheet's list */
  index?: number;
  range?: string;
  type?: string;
  operator?: string;
  /** as Excel stores it, no leading '=': "A,B" quoted for an inline list, $AI$2:$AI$5, 45000 (date serial), … */
  formula1?: string;
  formula2?: string;
  allowBlank?: boolean;
  inCellDropdown?: boolean;
  showInputMessage?: boolean;
  inputTitle?: string;
  inputMessage?: string;
  showErrorMessage?: boolean;
  errorStyle?: string;
  errorTitle?: string;
  errorMessage?: string;
  /** audit wording */
  description?: string;
  oldDescription?: string;
}
export interface SheetEdits {
  cells: CellEdit[];
  merges: MergeEdit[];
  columns: SizeEdit[];
  rows: SizeEdit[];
  conditionalFormats: ConditionalFormatEdit[];
  /** Data Validation Rules Manager changes (the template editor only) */
  dataValidations?: DataValidationEdit[];
}

export const EMPTY_EDITS: SheetEdits = { cells: [], merges: [], columns: [], rows: [], conditionalFormats: [] };

export function isEmptyEdits(e: SheetEdits): boolean {
  return !e.cells.length && !e.merges.length && !e.columns.length && !e.rows.length && !e.conditionalFormats.length
    && !(e.dataValidations?.length);
}

// ── Univer snapshot (loose) ──────────────────────────────────────────────────

type Rgb = { rgb?: string | null } | null | undefined;
type BorderData = { s?: number; cl?: Rgb } | null | undefined;
export interface UStyle {
  ff?: string | null; fs?: number; it?: number; bl?: number;
  ul?: { s?: number } | null; st?: { s?: number } | null;
  cl?: Rgb; bg?: Rgb;
  bd?: { t?: BorderData; b?: BorderData; l?: BorderData; r?: BorderData } | null;
  ht?: number; vt?: number; tb?: number;
  n?: { pattern?: string } | null;
}
export interface UCell { v?: unknown; f?: string | null; si?: string | null; t?: number; s?: string | UStyle | null }
export interface USheet {
  name?: string;
  cellData?: Record<string, Record<string, UCell>>;
  mergeData?: { startRow: number; startColumn: number; endRow: number; endColumn: number }[];
  columnData?: Record<string, { w?: number; hd?: number }>;
  rowData?: Record<string, { h?: number; hd?: number; ia?: number }>;
}
export interface USnapshot { sheetOrder?: string[]; sheets?: Record<string, USheet>; styles?: Record<string, UStyle | null> }

// ── helpers ──────────────────────────────────────────────────────────────────

export function colName(col0: number): string {
  let n = col0 + 1;
  let s = '';
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}
export const a1 = (row0: number, col0: number) => `${colName(col0)}${row0 + 1}`;

/** Any CSS colour Univer emits (#rgb, #rrggbb, rgb(r,g,b)) → #RRGGBB, else null. */
export function normColor(c: string | null | undefined): string | null {
  if (!c) return null;
  const s = c.trim();
  let m = /^#([0-9a-f]{3})$/i.exec(s);
  if (m) return `#${m[1]!.split('').map(ch => ch + ch).join('')}`.toUpperCase();
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(s);
  if (m) return `#${m[1]}`.toUpperCase();
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  if (m) return `#${[m[1], m[2], m[3]].map(x => Math.min(255, Number(x)).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
  return null;
}

/** Univer BorderStyleTypes → OOXML border style. */
const BORDER_STYLES: Record<number, string> = {
  0: 'none', 1: 'thin', 2: 'hair', 3: 'dotted', 4: 'dashed', 5: 'dashDot', 6: 'dashDotDot', 7: 'double',
  8: 'medium', 9: 'mediumDashed', 10: 'mediumDashDot', 11: 'mediumDashDotDot', 12: 'slantDashDot', 13: 'thick',
};
const H_ALIGN: Record<number, string> = { 0: 'general', 1: 'left', 2: 'center', 3: 'right', 4: 'justify', 5: 'justify', 6: 'center' };
const V_ALIGN: Record<number, string> = { 0: 'bottom', 1: 'top', 2: 'center', 3: 'bottom' };

function resolveStyle(s: UCell['s'], styles: USnapshot['styles']): UStyle {
  if (!s) return {};
  if (typeof s === 'string') return styles?.[s] ?? {};
  return s;
}

const flag = (v: number | undefined | null) => v === 1;

/** The properties of `cur` that differ from `base`, as an OOXML-ready delta; undefined when none. */
export function styleDelta(base: UStyle, cur: UStyle): StyleDelta | undefined {
  const d: StyleDelta = {};

  const font: FontDelta = {};
  if ((base.ff ?? null) !== (cur.ff ?? null) && cur.ff) font.name = cur.ff;
  if ((base.fs ?? null) !== (cur.fs ?? null) && typeof cur.fs === 'number') font.size = cur.fs;
  if (flag(base.bl) !== flag(cur.bl)) font.bold = flag(cur.bl);
  if (flag(base.it) !== flag(cur.it)) font.italic = flag(cur.it);
  if (flag(base.ul?.s) !== flag(cur.ul?.s)) font.underline = flag(cur.ul?.s);
  if (flag(base.st?.s) !== flag(cur.st?.s)) font.strike = flag(cur.st?.s);
  const bcl = normColor(base.cl?.rgb), ccl = normColor(cur.cl?.rgb);
  if (bcl !== ccl) font.color = ccl ?? '';
  if (Object.keys(font).length) d.font = font;

  const bbg = normColor(base.bg?.rgb), cbg = normColor(cur.bg?.rgb);
  if (bbg !== cbg) d.fill = { color: cbg };

  const border: NonNullable<StyleDelta['border']> = {};
  const sides = [['t', 'top'], ['b', 'bottom'], ['l', 'left'], ['r', 'right']] as const;
  for (const [k, name] of sides) {
    const bs = base.bd?.[k], cs = cur.bd?.[k];
    const bStyle = bs?.s ?? 0, cStyle = cs?.s ?? 0;
    const bCol = normColor(bs?.cl?.rgb), cCol = normColor(cs?.cl?.rgb);
    if (bStyle !== cStyle || (cStyle !== 0 && bCol !== cCol))
      border[name] = cStyle === 0 ? { style: 'none' } : { style: BORDER_STYLES[cStyle] ?? 'thin', ...(cCol ? { color: cCol } : {}) };
  }
  if (Object.keys(border).length) d.border = border;

  const alignment: NonNullable<StyleDelta['alignment']> = {};
  if ((base.ht ?? 0) !== (cur.ht ?? 0)) alignment.horizontal = H_ALIGN[cur.ht ?? 0] ?? 'general';
  if ((base.vt ?? 0) !== (cur.vt ?? 0)) alignment.vertical = V_ALIGN[cur.vt ?? 0] ?? 'bottom';
  // Wrap = tb 3; the loader writes 2 (clip) for single-word wrapped headers, which is still "wrap" in the file.
  const bWrap = base.tb === 3 || base.tb === 2, cWrap = cur.tb === 3;
  if ((base.tb ?? 0) !== (cur.tb ?? 0) && bWrap !== cWrap) alignment.wrap = cWrap;
  if (Object.keys(alignment).length) d.alignment = alignment;

  const bn = base.n?.pattern ?? '', cn = cur.n?.pattern ?? '';
  if (bn !== cn) d.numberFormat = cn || 'General';

  return Object.keys(d).length ? d : undefined;
}

/** A cell's text as the designer saw it (audit "old value"). */
function cellText(c: UCell | undefined, formula: string | null): string | null {
  if (formula) return formula;
  const v = c?.v;
  return v == null || v === '' ? null : String(v);
}

const FIELD_TOKEN = /^\[([A-Za-z0-9_%#.\- ]+)\]$/;

/**
 * Diffs the live sheet against the load-time baseline.
 * `formulaOf` reads the LIVE formula of a cell (Univer shares formulas by id, so a
 * filled-down cell has no `f` of its own — the facade resolves it).
 * `sheetIndexOf` maps a sheet NAME to its 1-based position in the WORKBOOK (the
 * loader skips hidden sheets, so Univer's order is not the workbook's).
 */
export function diffSnapshots(
  base: USnapshot,
  cur: USnapshot,
  sheetIndexOf: (name: string) => number,
  formulaOf: (sheetName: string, row: number, col: number) => string | null,
): SheetEdits {
  const out: SheetEdits = { cells: [], merges: [], columns: [], rows: [], conditionalFormats: [] };
  const baseByName = new Map<string, USheet>();
  for (const id of base.sheetOrder ?? Object.keys(base.sheets ?? {})) {
    const s = base.sheets?.[id];
    if (s?.name) baseByName.set(s.name, s);
  }

  for (const id of cur.sheetOrder ?? Object.keys(cur.sheets ?? {})) {
    const cs = cur.sheets?.[id];
    if (!cs?.name) continue;
    const bs = baseByName.get(cs.name);
    if (!bs) continue;                                   // a sheet added in the browser is not supported
    const sheetIndex = sheetIndexOf(cs.name);
    if (sheetIndex < 1) continue;

    // ── cells ──
    const keys = new Set<string>();
    for (const [r, row] of Object.entries(bs.cellData ?? {})) for (const c of Object.keys(row ?? {})) keys.add(`${r},${c}`);
    for (const [r, row] of Object.entries(cs.cellData ?? {})) for (const c of Object.keys(row ?? {})) keys.add(`${r},${c}`);
    for (const key of keys) {
      const [r, c] = key.split(',').map(Number) as [number, number];
      const b = bs.cellData?.[r]?.[c];
      const k = cs.cellData?.[r]?.[c];
      const bFormula = b?.f || null;
      const kIsFormula = !!(k?.f || k?.si);
      const bIsFormula = !!(b?.f || b?.si);

      let contentChanged: boolean;
      if (kIsFormula || bIsFormula) {
        // A formula cell's value is recalculated — compare the formula only.
        contentChanged = kIsFormula !== bIsFormula || (k?.f ?? null) !== (b?.f ?? null) || (k?.si ?? null) !== (b?.si ?? null);
      } else {
        const bv = b?.v == null ? '' : String(b.v), kv = k?.v == null ? '' : String(k.v);
        contentChanged = bv !== kv;
      }

      const delta = styleDelta(resolveStyle(b?.s, base.styles), resolveStyle(k?.s, cur.styles));
      if (!contentChanged && !delta) continue;

      const edit: CellEdit = { sheetIndex, ref: a1(r, c), contentChanged };
      if (contentChanged) {
        edit.oldText = cellText(b, bFormula);
        if (kIsFormula) {
          edit.formula = formulaOf(cs.name, r, c) ?? k?.f ?? null;
        } else {
          const v = k?.v;
          const text = v == null ? '' : String(v);
          const token = FIELD_TOKEN.exec(text.trim());
          if (token) {
            // Studio convention: typing [NAME] means the merge field NAME.
            edit.formula = `=FIELD("${token[1]!.trim().toUpperCase()}")`;
          } else {
            edit.value = text === '' ? null : text;
            edit.valueType = typeof v === 'number' || k?.t === 2 ? 'number' : typeof v === 'boolean' || k?.t === 3 ? 'boolean' : 'string';
            if (edit.valueType === 'number' && !Number.isFinite(Number(text))) edit.valueType = 'string';
            if (edit.valueType === 'boolean') edit.value = text === 'true' || text === '1' || text === 'TRUE' ? 'true' : 'false';
          }
        }
      }
      if (delta) edit.style = delta;
      out.cells.push(edit);
    }

    // ── merges ──
    const mkey = (m: { startRow: number; startColumn: number; endRow: number; endColumn: number }) =>
      `${a1(m.startRow, m.startColumn)}:${a1(m.endRow, m.endColumn)}`;
    const bm = new Set((bs.mergeData ?? []).map(mkey));
    const km = new Set((cs.mergeData ?? []).map(mkey));
    for (const m of bm) if (!km.has(m)) out.merges.push({ sheetIndex, range: m, remove: true });
    for (const m of km) if (!bm.has(m)) out.merges.push({ sheetIndex, range: m, remove: false });

    // ── sizes ──
    const idx = new Set([...Object.keys(bs.columnData ?? {}), ...Object.keys(cs.columnData ?? {})]);
    for (const i of idx) {
      const bw = bs.columnData?.[i], kw = cs.columnData?.[i];
      if (bw?.hd === 1 || kw?.hd === 1 || kw?.w == null) continue;
      if (Math.round(kw.w) !== Math.round(bw?.w ?? -1)) out.columns.push({ sheetIndex, index: Number(i) + 1, pixels: Math.round(kw.w) });
    }
    const rdx = new Set([...Object.keys(bs.rowData ?? {}), ...Object.keys(cs.rowData ?? {})]);
    for (const i of rdx) {
      const bh = bs.rowData?.[i], kh = cs.rowData?.[i];
      if (bh?.hd === 1 || kh?.hd === 1 || kh?.h == null || kh.ia === 1) continue;   // auto-height is not a designer choice
      if (Math.round(kh.h) !== Math.round(bh?.h ?? -1)) out.rows.push({ sheetIndex, index: Number(i) + 1, pixels: Math.round(kh.h) });
    }
  }
  return out;
}
